import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Mail, ArrowLeft, CheckCircle2, AlertCircle, Loader2 } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { useTranslation } from 'react-i18next';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const ForgotPasswordModal = ({ isOpen, onClose, onBack }) => {
  const { t } = useTranslation();
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email) {
      setError(t('forgot_password.enter_email'));
      return;
    }

    setLoading(true);
    setError('');

    try {
      // Send origin_url so backend knows where to point the reset link
      const origin_url = window.location.origin;
      await axios.post(`${API}/auth/forgot-password`, { email, origin_url });
      setSent(true);
    } catch (err) {
      setError(t('forgot_password.error'));
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setEmail('');
    setSent(false);
    setError('');
    onClose();
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
        onClick={handleClose}
      >
        <motion.div
          initial={{ scale: 0.95, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.95, opacity: 0 }}
          className="bg-card rounded-2xl shadow-xl max-w-md w-full p-6"
          onClick={(e) => e.stopPropagation()}
          data-testid="forgot-password-modal"
        >
          {!sent ? (
            <>
              {/* Header */}
              <div className="flex items-center gap-3 mb-6">
                {onBack && (
                  <Button variant="ghost" size="icon" onClick={onBack} data-testid="back-to-login">
                    <ArrowLeft className="w-5 h-5" />
                  </Button>
                )}
                <div>
                  <h2 className="text-xl font-bold">{t('forgot_password.title')}</h2>
                  <p className="text-sm text-muted-foreground">{t('forgot_password.subtitle')}</p>
                </div>
              </div>

              {/* Form */}
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium mb-1">{t('email')}</label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                    <Input
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder={t('your_email')}
                      className="pl-10"
                      data-testid="forgot-password-email"
                    />
                  </div>
                </div>

                {error && (
                  <div className="flex items-center gap-2 text-red-500 text-sm">
                    <AlertCircle className="w-4 h-4" />
                    <span>{error}</span>
                  </div>
                )}

                <Button
                  type="submit"
                  className="w-full bg-local-sage hover:bg-local-sage/90"
                  disabled={loading}
                  data-testid="forgot-password-submit"
                >
                  {loading ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      {t('sending')}
                    </>
                  ) : (
                    t('forgot_password.send_link')
                  )}
                </Button>
              </form>
            </>
          ) : (
            /* Success State */
            <div className="text-center py-6">
              <div className="w-16 h-16 bg-green-100 dark:bg-green-900/30 rounded-full flex items-center justify-center mx-auto mb-4">
                <CheckCircle2 className="w-8 h-8 text-green-600" />
              </div>
              <h2 className="text-xl font-bold mb-2">{t('forgot_password.email_sent')}</h2>
              <p className="text-muted-foreground mb-6">
                {t('forgot_password.check_inbox')}
              </p>
              <Button onClick={handleClose} variant="outline" className="w-full">
                {t('close')}
              </Button>
            </div>
          )}
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default ForgotPasswordModal;
